/**
 * Shared enum-label lookup used by resource subtitle / detail-header
 * sites that need to render a localized label for a wire-form enum
 * value and FAIL LOUD on unknown values rather than silently leaking
 * the wire string into operator-facing UI.
 *
 * Class-wide-fix per CLAUDE.md §8 — extracted from
 * `webhookResource.tsx` (#316) after PR #343 R3 cross-feature grep
 * surfaced 4 sibling sites doing the same thing wrong (#345):
 *
 *   - `frontend/src/features/menu-planning/schema.tsx` subtitle
 *   - `frontend/src/features/dispatch/jobResource.tsx` subtitle
 *
 * The `syncJobResource.tsx` callsite went away with the frontend Sync
 * UI deletion in EPIC #503 Sub-PR D (ADR-0010 no-polling-sync), and the
 * driver-task subtitle went with the unreachable surface that owned it
 * (#2081) — the delivery job is the live dispatch-side consumer.
 *
 * EQD §3 alignment: silent fallback at internal boundaries is
 * forbidden. The `[unknown:${value}]` marker is the visible-prevent
 * layer for "new enum value added to the backend but the frontend
 * option table not updated" drift. The detector layer is the
 * cross-team `*-enums-sync.test.ts` family (#83, #156, #313).
 */

import i18n from '@/i18n';
import { lookupEnumLabel } from '@resource-kit/ui';

/**
 * The host binding of `@resource-kit/ui`'s `lookupEnumLabel` (#2169 PR-2): the
 * lookup itself moved, the i18next SINGLETON stayed.
 *
 * A wrapper rather than a re-export because SOME call sites sit outside any
 * React render: the module-level `detail.subtitle` callbacks in
 * `features/dispatch/jobResource.tsx`, `features/menu-planning/schema.tsx` and
 * `features/webhooks/webhookResource.tsx` are plain functions hanging off a
 * resource-bundle literal, with no `useResourceKit()` to read a translator
 * from — two of them, `jobResource.tsx` and `webhookResource.tsx`, already
 * reach for the `i18n` singleton in the same `detail` literal. The rest ARE
 * inside components and could thread `t` down, but binding once here is
 * cheaper than threading it through every caller and leaves all of the call
 * sites unchanged.
 */
export const lookupEnumLabelOrWarn = (
  value: string,
  options: ReadonlyArray<{ value: string | number; label: string }>,
  kind: string,
): string => lookupEnumLabel(value, options, kind, (key) => i18n.t(key));
